// 必填项：模块导入时 i18n 尚未初始化，直接写死中文提示避免显示 key
export const required = {
  required: true,
  message: '该项为必填项'
}

/**
 * 「不能是纯数字」规则工厂 —— 供**人工填写的名称/姓名**类字段使用
 *
 * 背景：`required` 挡不住纯数字（`123456` 是非空的），于是公司名称、姓名里
 * 能存进一串纯数字，后续既认不出、也无法与编号区分。
 *
 * 用法（与 required 并列，放在同一条 rules 数组里）：
 *   const formRules = reactive({
 *     name: [
 *       { required: true, message: '公司名称不能为空', trigger: 'blur' },
 *       notPureNumber('公司名称')
 *     ]
 *   })
 *
 * ★ 判定前先 `trim`，所以 `" 123 "` 同样算纯数字。
 * ★ 空值/空白一律放行，必填交给 `required` ⇒ 套到非必填字段上不会被误伤。
 * ★ 不要用在**型号类**字段上：产品名称 `18650`、分类 `18650` 是合法值，
 *   误加会挡住正常数据（这正是本规则只覆盖「主体名称/人名」的原因）。
 *
 * 实证（真实 async-validator@4.2.5，即 element-plus 的校验内核，15/15 符合预期）：
 *   `undefined` / `null` / `''` / `'   '`            → 放行
 *   `'123'` / `'1234567'` / `'１２３'` / `' 123 '`   → 拦截
 *   `'3M中国'` / `'360公司'` / `'123abc'` / `'010-1234'` / `'123 456'` → 放行
 *   ↑ 最后一行是反向断言：带数字的正常名称不会被误伤
 */
export const notPureNumber = (label: string) => ({
  validator: (_rule: unknown, value: unknown, callback: (error?: Error) => void) => {
    const text = value === null || value === undefined ? '' : String(value).trim()
    if (text && /^[0-9０-９]+$/.test(text)) {
      callback(new Error(`${label}不能为纯数字`))
      return
    }
    callback()
  },
  trigger: 'blur'
})

/** IPv4：四段十进制、每段 0~255。允许前导零（`010.1.1.1`），宽松接受、避免误伤历史数据 */
const isValidIpv4 = (text: string): boolean => {
  const parts = text.split('.')
  if (parts.length !== 4) {
    return false
  }
  return parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255)
}

/** IPv6 的单个分组：1~4 位十六进制 */
const IPV6_GROUP_REGEX = /^[0-9a-fA-F]{1,4}$/

/**
 * IPv6：支持完整写法（`2001:db8:0:0:0:0:0:1`）、`::` 压缩（`2001:db8::1`）、
 * 以及末尾 IPv4-mapped（`::ffff:192.168.1.1`）。
 *
 * ⚠️ **刻意不支持 zone id**（`fe80::1%eth0`）：CRM / IoT 的 IP 字段用不到它，
 *    支持它会连带放开 `%`、让字符过滤与正则一起复杂化。
 *    若将来真要支持，改这里 + `inputFilter.ts` 的 `onlyIpChars` 两处。
 */
const isValidIpv6 = (text: string): boolean => {
  if (!text.includes(':')) {
    return false
  }
  // 「::」最多出现一次
  const firstDoubleColon = text.indexOf('::')
  if (firstDoubleColon !== -1 && text.indexOf('::', firstDoubleColon + 1) !== -1) {
    return false
  }
  const hasDoubleColon = firstDoubleColon !== -1
  const head = hasDoubleColon ? text.slice(0, firstDoubleColon) : text
  const tail = hasDoubleColon ? text.slice(firstDoubleColon + 2) : ''
  const headGroups = head === '' ? [] : head.split(':')
  const tailGroups = tail === '' ? [] : tail.split(':')

  // 末尾允许 IPv4-mapped 写法；IPv4 折算占 2 个分组
  let ipv4Groups = 0
  const lastGroup = tailGroups.length
    ? tailGroups[tailGroups.length - 1]
    : headGroups[headGroups.length - 1]
  if (lastGroup && lastGroup.includes('.')) {
    if (!isValidIpv4(lastGroup)) {
      return false
    }
    ipv4Groups = 2
    if (tailGroups.length) {
      tailGroups.pop()
    } else {
      headGroups.pop()
    }
  }
  const groups = [...headGroups, ...tailGroups]
  if (groups.length && !groups.every((group) => IPV6_GROUP_REGEX.test(group))) {
    return false
  }
  const count = groups.length + ipv4Groups
  // 有「::」⇒ 至少压掉一组，总组数 < 8；没有 ⇒ 必须正好 8 组
  return hasDoubleColon ? count < 8 : count === 8
}

/** 是否为合法的 IPv4 / IPv6 字面量（不含端口、CIDR、协议前缀） */
export const isValidIp = (value: unknown): boolean => {
  const text = value === null || value === undefined ? '' : String(value).trim()
  if (!text) {
    return false
  }
  return isValidIpv4(text) || isValidIpv6(text)
}

/**
 * IP 地址格式规则工厂 —— 供 `IP` 类字段使用
 *
 * 用法（与 required 并列）：
 *   const formRules = reactive({ ip: [ipAddress('IP')] })
 *
 * ★ 空值/空白一律放行（必填交给 `required`）⇒ 套到非必填字段上不会被误伤。
 * ★ 只认**单个地址**：`192.168.1.1:8080`、`192.168.1.0/24`、`http://1.2.3.4` 都会被拦，
 *   这是刻意的 —— 字段语义是「IP」，带端口/网段应另开字段。
 *
 * 实证（真实 async-validator@4.2.5，即 element-plus 的校验内核；51 条用例全过）：
 *   放行：`127.0.0.1` / `192.168.1.1` / `0.0.0.0` / `255.255.255.255`
 *        / `::1` / `::` / `2001:db8::1` / `fe80::1` / `1:2:3:4:5:6:7:8` / `::ffff:192.168.1.1`
 *        / `undefined` / `null` / `''` / `'   '`
 *   拦截：`256.1.1.1` / `1.2.3` / `1.2.3.4.5` / `1.2.3.4:8080` / `192.168.1.0/24`
 *        / `abc` / `1.2.3.4abc` / `1:2:3:4:5:6:7:8:9` / `1::2::3` / `:::` / `fe80::1%eth0`
 */
export const ipAddress = (label = 'IP') => ({
  validator: (_rule: unknown, value: unknown, callback: (error?: Error) => void) => {
    const text = value === null || value === undefined ? '' : String(value).trim()
    if (text && !isValidIp(text)) {
      callback(new Error(`${label}格式不正确，请输入 IPv4 / IPv6 地址`))
      return
    }
    callback()
  },
  trigger: 'blur'
})
